import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Chip from '@mui/material/Chip';
import type { ReactElement } from 'react';

/** How a state reads at a glance. The word always carries the meaning. */
export type StatusTone = 'neutral' | 'success' | 'warning' | 'error';

const ICONS: Record<StatusTone, ReactElement> = {
  neutral: <RadioButtonUncheckedIcon />,
  success: <CheckCircleOutlineIcon />,
  warning: <WarningAmberIcon />,
  error: <ErrorOutlineIcon />,
};

const COLOURS: Record<StatusTone, 'default' | 'success' | 'warning' | 'error'> = {
  neutral: 'default',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

interface StatusChipProps {
  /** The state in words: "Draft", "Published", "Hidden". */
  label: string;
  tone?: StatusTone;
  /** On the chip itself. Required, so a test can always find it. */
  testId: string;
  /** Filled rather than outlined, for the one state a card is about. */
  filled?: boolean;
}

/**
 * A state as a small chip: its word, an icon, and a colour that only repeats
 * what the word says, so nobody has to tell green from orange to read it
 * (WCAG 2.1 AA, 1.4.1).
 *
 * One component for every lifecycle in the portal - the ARC's status, a
 * section's visibility, a page's or a survey's state - so they look alike
 * wherever they appear.
 */
export function StatusChip({
  label,
  tone = 'neutral',
  testId,
  filled = false,
}: StatusChipProps) {
  return (
    <Chip
      size="small"
      variant={filled ? 'filled' : 'outlined'}
      icon={ICONS[tone]}
      label={label}
      color={COLOURS[tone]}
      data-testid={testId}
      data-tone={tone}
    />
  );
}
